import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { ArticleBody } from '../ArticleBody';
import { HubSignIn, useAuthMode } from '../HubSignIn';
import { useToast } from '../ui/Toast';
import { PromptDialog } from '../ui/PromptDialog';
import { Badge, EmptyState, Field, LoadingPanel } from '../ui/bits';

/**
 * Client Portal v1 (DESIGN §11.2) — deliberately thin: approvals, calendar,
 * KPIs, requests, connection status. Auth is the same session as /app (dev
 * login until the Hub session flip).
 */

interface PortalTenant {
  id: string;
  slug: string;
  displayName: string;
  role: string;
}
interface Me {
  user: { email: string; name: string; realm: string };
  tenants: PortalTenant[];
}
interface PortalItem {
  id: string;
  type: string;
  channel: string | null;
  state: string;
  scheduledFor: string | null;
  body?: string;
  title?: string;
  bodyMarkdown?: string;
  metaDescription?: string;
  category?: string;
  hashtags?: string[];
  media?: { key: string; disclosure?: string };
  needsPhoto?: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  social_post: 'Social post',
  gbp_post: 'Google Business post',
  seo_article: 'Article',
  email_campaign: 'Email',
};

const TABS = ['approvals', 'calendar', 'performance', 'reports', 'requests'] as const;
type Tab = (typeof TABS)[number];

export function PortalApp() {
  const [me, setMe] = useState<Me | null | 'anon'>(null);
  const [tenant, setTenant] = useState<PortalTenant | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(rawTab ?? '') ? (rawTab as Tab) : 'approvals';
  const setTab = (next: Tab) => setSearchParams({ tab: next });

  const loadMe = useCallback(() => {
    api
      .get<Me>('/api/portal/me')
      .then(async (data) => {
        if (data.user.realm === 'ops' && data.tenants.length === 0) {
          // ops preview: browse any tenant's portal
          const all = await api.get<Array<{ id: string; slug: string; displayName: string }>>('/api/admin/tenants');
          data = { ...data, tenants: all.map((t) => ({ ...t, role: 'approver (ops preview)' })) };
        }
        setMe(data);
        setTenant((current) => current ?? data.tenants[0] ?? null);
      })
      .catch(() => setMe('anon'));
  }, []);
  useEffect(loadMe, [loadMe]);

  if (me === null) return <LoadingPanel label="Loading your portal…" />;
  if (me === 'anon') return <PortalLogin onLoggedIn={loadMe} />;
  const signOut = () => api.post('/api/auth/logout', {}).then(() => window.location.reload());
  if (!tenant)
    return (
      <div className="auth-card">
        <h1>Client portal</h1>
        <p className="muted">
          No client workspace is linked to {me.user.email}. Contact your Plaidware account manager.
        </p>
        <button type="button" onClick={() => void signOut()}>Sign in as a different user</button>
      </div>
    );

  return (
    <div className="portal-shell">
      <header className="row-baseline" style={{ gap: 12 }}>
        <h1 style={{ margin: 0 }}>{tenant.displayName}</h1>
        <span className="muted">Marketing portal · {me.user.email}</span>
        <button type="button" className="btn-sm end" onClick={() => void signOut()}>
          Sign out
        </button>
        {me.tenants.length > 1 && (
          <select
            value={tenant.id}
            aria-label="Workspace"
            onChange={(e) => setTenant(me.tenants.find((t) => t.id === e.target.value) ?? tenant)}
          >
            {me.tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.displayName}
              </option>
            ))}
          </select>
        )}
      </header>
      <nav className="tabs" aria-label="Portal sections">
        {TABS.map((t) => (
          <button key={t} type="button" className={`tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
            {t[0]!.toUpperCase() + t.slice(1)}
          </button>
        ))}
      </nav>
      {tab === 'approvals' && <Approvals tenant={tenant} canDecide={tenant.role !== 'viewer'} />}
      {tab === 'calendar' && <Calendar tenant={tenant} />}
      {tab === 'performance' && <Performance tenant={tenant} />}
      {tab === 'reports' && <Reports tenant={tenant} />}
      {tab === 'requests' && <Requests tenant={tenant} />}
    </div>
  );
}

function PortalLogin({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const authMode = useAuthMode();
  if (authMode === null) return <LoadingPanel />;
  if (authMode.mode === 'hub' && authMode.hubBaseUrl) {
    return <HubSignIn hubBaseUrl={authMode.hubBaseUrl} onSignedIn={onLoggedIn} title="Client portal" />;
  }
  return (
    <div className="auth-card">
      <h1>Client portal</h1>
      <p className="muted">Sign in with your email.</p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          api
            .post('/api/auth/dev-login', { email })
            .then(onLoggedIn)
            .catch(() => setError('No account found for that email.'));
        }}
      >
        <Field label="Email" error={error}>
          {(id) => (
            <input
              id={id}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@business.com"
            />
          )}
        </Field>
        <div>
          <button className="primary" type="submit">
            Sign in
          </button>
        </div>
      </form>
    </div>
  );
}

function ItemCard({
  item,
  tenant,
  actions,
}: {
  item: PortalItem;
  tenant: PortalTenant;
  actions?: React.ReactNode;
}) {
  return (
    <div className="panel" style={{ marginBottom: 12 }}>
      <div className="row-baseline">
        <strong>{TYPE_LABELS[item.type] ?? item.type}</strong>
        {item.category && <span className="muted">{item.category.replaceAll('_', ' ')}</span>}
        {item.scheduledFor && (
          <span className="muted small">
            planned {new Date(item.scheduledFor).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
          </span>
        )}
        <Badge state={item.state} />
      </div>
      {item.media?.key && (
        <figure style={{ margin: '8px 0' }}>
          <img
            src={`/api/portal/media?tenantId=${tenant.id}&key=${encodeURIComponent(item.media.key)}`}
            alt={item.media.disclosure ?? 'illustration'}
            style={{ maxWidth: 240, borderRadius: 8 }}
          />
          {item.media.disclosure && <figcaption className="muted tiny">{item.media.disclosure}</figcaption>}
        </figure>
      )}
      {item.title && <h3 style={{ margin: '10px 0 2px' }}>{item.title}</h3>}
      {item.metaDescription && (
        <p className="muted small" style={{ margin: '2px 0 8px' }}>
          {item.type === 'email_campaign' ? 'Inbox preview' : 'Search preview'}: {item.metaDescription}
        </p>
      )}
      {item.bodyMarkdown && (
        <div className="article-scroll" style={{ margin: '8px 0' }}>
          <ArticleBody markdown={item.bodyMarkdown} />
        </div>
      )}
      {item.body && <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0' }}>{item.body}</p>}
      {item.hashtags && item.hashtags.length > 0 && (
        <p className="small" style={{ color: 'var(--accent)' }}>{item.hashtags.join(' ')}</p>
      )}
      {actions}
    </div>
  );
}

function Approvals({ tenant, canDecide }: { tenant: PortalTenant; canDecide: boolean }) {
  const [items, setItems] = useState<PortalItem[] | null>(null);
  const [rejecting, setRejecting] = useState<PortalItem | null>(null);
  const toast = useToast();
  const load = useCallback(
    () =>
      api
        .get<PortalItem[]>(`/api/portal/approvals?tenantId=${tenant.id}`)
        .then(setItems)
        .catch(console.error),
    [tenant.id]
  );
  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (item: PortalItem, decision: 'approve' | 'reject', comment?: string) => {
    try {
      await api.post(`/api/portal/work-items/${item.id}/decision`, {
        tenantId: tenant.id,
        decision,
        reason: decision === 'reject' ? 'other' : undefined,
        comment,
      });
      toast.success(decision === 'approve' ? 'Approved — it will publish as scheduled.' : 'Sent back with your feedback.');
      void load();
    } catch {
      toast.error('That did not go through — please try again.');
    }
  };

  if (items === null) return <LoadingPanel />;
  return (
    <div>
      {items.length === 0 && (
        <div className="panel">
          <EmptyState title="Nothing waiting for your approval 🎉" hint="New drafts appear here when they're ready for you." />
        </div>
      )}
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          tenant={tenant}
          actions={
            canDecide ? (
              <div className="row">
                <button type="button" className="primary" onClick={() => void decide(item, 'approve')}>
                  Approve
                </button>
                <button type="button" onClick={() => setRejecting(item)}>Request changes</button>
              </div>
            ) : (
              <p className="muted small">An approver on your team can act on this.</p>
            )
          }
        />
      ))}
      <PromptDialog
        open={rejecting !== null}
        title="Request changes"
        label="What should we change?"
        placeholder="e.g. Make it longer and mention our weekend hours"
        submitLabel="Send feedback"
        onSubmit={(comment) => {
          if (rejecting) void decide(rejecting, 'reject', comment || undefined);
        }}
        onClose={() => setRejecting(null)}
      />
    </div>
  );
}

function Calendar({ tenant }: { tenant: PortalTenant }) {
  const [items, setItems] = useState<PortalItem[] | null>(null);
  useEffect(() => {
    api
      .get<PortalItem[]>(`/api/portal/calendar?tenantId=${tenant.id}`)
      .then(setItems)
      .catch(console.error);
  }, [tenant.id]);
  if (items === null) return <LoadingPanel />;
  if (items.length === 0)
    return (
      <div className="panel">
        <EmptyState title="No scheduled content in this window yet" hint="Approved posts show up here with their planned dates." />
      </div>
    );
  return (
    <div>
      {items.map((item) => (
        <ItemCard key={item.id} item={item} tenant={tenant} />
      ))}
    </div>
  );
}

function Performance({ tenant }: { tenant: PortalTenant }) {
  interface Kpis {
    period: string;
    reportOrder: string[];
    kpis: Array<{ key: string; label: string; formula: string; value: number | null; byDim?: Record<string, number> }>;
    funnel: Array<{ stage: string; label: string; count: number }>;
  }
  const [data, setData] = useState<Kpis | null>(null);
  useEffect(() => {
    api.get<Kpis>(`/api/portal/kpis?tenantId=${tenant.id}`).then(setData).catch(console.error);
  }, [tenant.id]);
  if (!data) return <LoadingPanel />;
  const fmt = (k: Kpis['kpis'][number]) => {
    if (k.value === null) return '—';
    if (/^(cp|revenue|spend)/.test(k.key)) return `$${(k.value / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (k.formula.includes('/')) return `${(k.value * 100).toFixed(0)}%`;
    return k.value.toLocaleString();
  };
  const ordered = data.reportOrder
    .map((key) => data.kpis.find((k) => k.key === key))
    .filter((k): k is NonNullable<typeof k> => Boolean(k));
  return (
    <div className="panel">
      <h3>This month ({data.period})</h3>
      <div className="row" style={{ gap: 20, marginBottom: 14 }}>
        {data.funnel
          .filter((f) => f.count > 0)
          .map((f) => (
            <div key={f.stage} className="stat" style={{ textAlign: 'center', minWidth: 0 }}>
              <div className="stat-num">{f.count}</div>
              <div className="stat-lbl">{f.label}</div>
            </div>
          ))}
      </div>
      <div className="row" style={{ gap: 22 }}>
        {ordered.map((k) => (
          <div key={k.key}>
            <strong>{fmt(k)}</strong> <span className="stat-lbl">{k.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Reports({ tenant }: { tenant: PortalTenant }) {
  const [rows, setRows] = useState<Array<{ id: string; period: string; pdfKey: string | null; deliveredAt: string | null }> | null>(null);
  useEffect(() => {
    api
      .get<typeof rows>(`/api/portal/reports?tenantId=${tenant.id}`)
      .then((r) => setRows(r ?? []))
      .catch(console.error);
  }, [tenant.id]);
  if (rows === null) return <LoadingPanel />;
  if (rows.length === 0)
    return (
      <div className="panel">
        <EmptyState title="Your monthly reports will appear here" hint="A new report arrives at the start of each month." />
      </div>
    );
  return (
    <div className="panel">
      {rows.map((r) => (
        <p key={r.id}>
          <strong>{r.period}</strong>
          {' · '}
          {r.pdfKey ? (
            <a
              href={`/api/portal/report-pdf?tenantId=${tenant.id}&key=${encodeURIComponent(r.pdfKey)}`}
              target="_blank"
              rel="noreferrer"
            >
              Download PDF
            </a>
          ) : (
            <span className="muted">processing</span>
          )}
          {r.deliveredAt && (
            <span className="muted small"> · delivered {new Date(r.deliveredAt).toLocaleDateString()}</span>
          )}
        </p>
      ))}
    </div>
  );
}

function Requests({ tenant }: { tenant: PortalTenant }) {
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const toast = useToast();
  return (
    <div className="panel" style={{ maxWidth: 560 }}>
      <h3>Ask us anything</h3>
      <p className="muted">
        Running a promotion? Want something featured? Tell the team — it lands directly in our work queue.
      </p>
      {sent ? (
        <p className="callout callout--ok">Sent — the team will pick this up shortly. ✓</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            api
              .post('/api/portal/requests', { tenantId: tenant.id, message })
              .then(() => setSent(true))
              .catch(() => toast.error('Your message did not send — please try again.'));
          }}
        >
          <textarea
            required
            minLength={5}
            rows={4}
            value={message}
            aria-label="Your request"
            onChange={(e) => setMessage(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
            placeholder="e.g. We're running a $99 fall tune-up special through October — can we promote it?"
          />
          <button className="primary" type="submit">
            Send to the team
          </button>
        </form>
      )}
    </div>
  );
}
